/**
 * 上传文件按钮组件
 * 包含上传图标按钮 + 提示气泡（禁用时显示禁用提示，正常时显示上传引导）
 *
 * 显示条件由父组件控制（visible 属性）：
 * - 非录音状态
 * - 非 dsReport 模板
 * - 非 taggingNode 或在海报模式下
 * - isShowDownload 或 isPosterMode
 * - currentData 不强制隐藏文件上传
 */
import React, { useRef } from 'react';
import { useHover } from 'ahooks';
import { useIntl } from 'umi';
import classNames from 'classnames';
import CommonBubble from '@home/components/CommonBubble';
import UploadPlusIcon from '@/pages/aigc/assets/svg/agent_add_file.svg';
import UploadPlusActiveIcon from '@/pages/aigc/assets/svg/agent_add_file-active.svg';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';

interface IUploadFileButtonProps {
  /** 是否显示按钮（由父组件根据多个条件计算） */
  visible: boolean;
  /** 是否禁用文件上传（agent 配置等导致） */
  disableFileUpload: boolean;
  /** 图片上传是否禁用（受张数等限制） */
  imgUploadDisabled: boolean;
  /** 用于禁用提示文案的 agent 名称 */
  agentName?: string;
  /** 点击上传 */
  onClick: () => void;
}

const UploadFileButton: React.FC<IUploadFileButtonProps> = ({
  visible,
  disableFileUpload,
  imgUploadDisabled,
  agentName,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const hovered = useHover(uploadButtonRef);
  const isDisabled = imgUploadDisabled || disableFileUpload;

  if (!visible) return null;

  return (
    <CommonBubble
      placement="top"
      content={
        disableFileUpload
          ? formatMessage(
            { id: 'dba_upload_img_disabled_tips' },
            { agentName },
          )
          : (
            <div>
              <div>{formatMessage({ id: 'dba_upload_img_title' })}</div>
              <FormatHtmlTextToReact text={formatMessage({ id: 'dba_upload_img_tips' })} />
            </div>
          )
      }
      trigger="hover"
      className="dba-upload-icon-tip"
      overlayClassName='dba-nav-menu-overlay'
    >
      <button
        ref={uploadButtonRef}
        className={classNames('dba-upload-icon', {
          'dba-upload-icon-disabled': isDisabled,
        })}
        onClick={onClick}
        disabled={disableFileUpload}
      >
        <img
          src={hovered && !isDisabled ? UploadPlusActiveIcon : UploadPlusIcon}
          alt=""
        />
      </button>
    </CommonBubble>
  );
};

export default UploadFileButton;
